import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Mail, Calendar, MessageSquare, HelpCircle, Users, BarChart3, CheckCircle, Clock, XCircle, Eye, Trash2, Newspaper, Plus, Edit, EyeOff, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  adminLogin, 
  verifyAdminToken, 
  getAdminStats,
  getContacts,
  updateContactStatus,
  deleteContact,
  getBookings,
  updateBookingStatus,
  deleteBooking
} from '@/services/api';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [pressArticles, setPressArticles] = useState([]);
  const [partners, setPartners] = useState([]);
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    subtitle: '',
    content: '',
    category: 'news',
    image_url: '',
    author: 'Väternotruf.com'
  });
  const [partnerForm, setPartnerForm] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    category: 'partner',
    logo_url: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      try {
        await verifyAdminToken();
        setIsAuthenticated(true);
        await loadData();
      } catch (error) {
        localStorage.removeItem('admin_token');
      }
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await adminLogin(password);
      localStorage.setItem('admin_token', response.token);
      setIsAuthenticated(true);
      toast.success('Erfolgreich angemeldet');
      await loadData();
    } catch (error) {
      toast.error('Falsches Passwort');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setPassword('');
    toast.success('Abgemeldet');
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [statsData, contactsData, bookingsData, pressData, partnersData] = await Promise.all([
        getAdminStats(),
        getContacts(),
        getBookings(),
        axios.get(`${BACKEND_URL}/api/press/admin/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.data).catch(() => []),
        axios.get(`${BACKEND_URL}/api/partners/admin/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.data).catch(() => [])
      ]);
      setStats(statsData);
      setContacts(contactsData);
      setBookings(bookingsData);
      setPressArticles(pressData);
      setPartners(partnersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    }
  };

  const handleContactStatusChange = async (contactId, status) => {
    try {
      await updateContactStatus(contactId, status);
      setContacts(contacts.map(c => c.id === contactId ? { ...c, status } : c));
      toast.success('Status aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Kontakt wirklich löschen?')) return;
    try {
      await deleteContact(contactId);
      setContacts(contacts.filter(c => c.id !== contactId));
      toast.success('Kontakt gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleBookingStatusChange = async (bookingId, status) => {
    try {
      await updateBookingStatus(bookingId, status);
      setBookings(bookings.map(b => b.id === bookingId ? { ...b, status } : b));
      toast.success('Status aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Termin wirklich löschen?')) return;
    try {
      await deleteBooking(bookingId);
      setBookings(bookings.filter(b => b.id !== bookingId));
      toast.success('Termin gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Press article functions
  const openArticleDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title,
        subtitle: article.subtitle || '',
        content: article.content,
        category: article.category,
        image_url: article.image_url || '',
        author: article.author
      });
    } else {
      setEditingArticle(null);
      setArticleForm({
        title: '',
        subtitle: '',
        content: '',
        category: 'news',
        image_url: '',
        author: 'Väternotruf.com'
      });
    }
    setShowArticleDialog(true);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title || !articleForm.content) {
      toast.error('Titel und Inhalt sind erforderlich');
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    try {
      if (editingArticle) {
        await axios.put(`${BACKEND_URL}/api/press/${editingArticle.id}`, articleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Artikel aktualisiert');
      } else {
        await axios.post(`${BACKEND_URL}/api/press`, articleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Artikel erstellt');
      }
      setShowArticleDialog(false);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleTogglePublish = async (articleId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.patch(`${BACKEND_URL}/api/press/${articleId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPressArticles(pressArticles.map(a => 
        a.id === articleId ? { ...a, published: response.data.published } : a
      ));
      toast.success(response.data.published ? 'Artikel veröffentlicht' : 'Artikel zurückgezogen');
    } catch (error) {
      toast.error('Fehler beim Ändern');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Artikel wirklich löschen?')) return;
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/press/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPressArticles(pressArticles.filter(a => a.id !== articleId));
      toast.success('Artikel gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Partner functions
  const openPartnerDialog = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setPartnerForm({
        name: partner.name,
        description: partner.description,
        address: partner.address || '',
        phone: partner.phone || '',
        email: partner.email || '',
        website: partner.website || '',
        category: partner.category,
        logo_url: partner.logo_url || ''
      });
    } else {
      setEditingPartner(null);
      setPartnerForm({
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        category: 'partner',
        logo_url: ''
      });
    }
    setShowPartnerDialog(true);
  };

  const handleSavePartner = async () => {
    if (!partnerForm.name || !partnerForm.description) {
      toast.error('Name und Beschreibung sind erforderlich');
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    try {
      if (editingPartner) {
        await axios.put(`${BACKEND_URL}/api/partners/${editingPartner.id}`, partnerForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Partner aktualisiert');
      } else {
        await axios.post(`${BACKEND_URL}/api/partners`, partnerForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Partner erstellt');
      }
      setShowPartnerDialog(false);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleTogglePartner = async (partnerId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.patch(`${BACKEND_URL}/api/partners/${partnerId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(partners.map(p => 
        p.id === partnerId ? { ...p, active: response.data.active } : p
      ));
      toast.success(response.data.active ? 'Partner aktiviert' : 'Partner deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Ändern');
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Partner wirklich löschen?')) return;
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(partners.filter(p => p.id !== partnerId));
      toast.success('Partner gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getPartnerCategoryLabel = (category) => {
    const labels = {
      'lawyer': 'Rechtsanwalt',
      'counseling': 'Beratungsstelle',
      'organization': 'Organisation',
      'partner': 'Partner',
    };
    return labels[category] || 'Partner';
  };

  const getStatusBadge = (status, type) => {
    const contactStyles = {
      new: { label: 'Neu', className: 'bg-blue-100 text-blue-700' },
      read: { label: 'Gelesen', className: 'bg-yellow-100 text-yellow-700' },
      replied: { label: 'Beantwortet', className: 'bg-green-100 text-green-700' }
    };
    const bookingStyles = {
      pending: { label: 'Ausstehend', className: 'bg-yellow-100 text-yellow-700' },
      confirmed: { label: 'Bestätigt', className: 'bg-blue-100 text-blue-700' },
      completed: { label: 'Abgeschlossen', className: 'bg-green-100 text-green-700' },
      cancelled: { label: 'Storniert', className: 'bg-red-100 text-red-700' }
    };
    const styles = type === 'contact' ? contactStyles : bookingStyles;
    const style = styles[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={style.className}>{style.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[hsl(210,40%,98%)]">
        <div className="text-[hsl(215,15%,45%)]">Lade...</div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[hsl(210,40%,98%)]">
        <Card className="w-full max-w-md border-[hsl(210,25%,90%)]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-[hsl(213,80%,35%,0.1)] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[hsl(213,80%,35%)]" />
            </div>
            <CardTitle className="text-[hsl(215,25%,15%)]">Admin-Bereich</CardTitle>
            <CardDescription>Bitte geben Sie das Passwort ein</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort eingeben"
                  className="border-[hsl(210,25%,88%)]"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white">
                Anmelden
              </Button>
            </form>
            <p className="text-xs text-[hsl(215,15%,60%)] text-center mt-4">
              Standard-Passwort: vaeternotruf2025
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen pt-20 bg-[hsl(210,40%,98%)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[hsl(215,25%,15%)]">Admin-Dashboard</h1>
            <p className="text-[hsl(215,15%,45%)]">Verwalten Sie Kontakte und Termine</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="border-[hsl(210,25%,88%)]">
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-[hsl(210,25%,90%)]">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[hsl(215,25%,15%)]">{stats.new_contacts}</p>
                    <p className="text-sm text-[hsl(215,15%,45%)]">Neue Anfragen</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-[hsl(210,25%,90%)]">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center mr-4">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[hsl(215,25%,15%)]">{stats.pending_bookings}</p>
                    <p className="text-sm text-[hsl(215,15%,45%)]">Offene Termine</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-[hsl(210,25%,90%)]">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mr-4">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[hsl(215,25%,15%)]">{stats.total_bookings_month}</p>
                    <p className="text-sm text-[hsl(215,15%,45%)]">Termine (Monat)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-[hsl(210,25%,90%)]">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mr-4">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[hsl(215,25%,15%)]">{stats.total_contacts_month}</p>
                    <p className="text-sm text-[hsl(215,15%,45%)]">Kontakte (Monat)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="contacts" className="space-y-6">
          <TabsList className="bg-white border border-[hsl(210,25%,90%)]">
            <TabsTrigger value="contacts" className="data-[state=active]:bg-[hsl(213,80%,35%)] data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" />
              Kontakte ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[hsl(213,80%,35%)] data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Termine ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="press" className="data-[state=active]:bg-[hsl(213,80%,35%)] data-[state=active]:text-white">
              <Newspaper className="w-4 h-4 mr-2" />
              Presse ({pressArticles.length})
            </TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-[hsl(213,80%,35%)] data-[state=active]:text-white">
              <Building className="w-4 h-4 mr-2" />
              Partner ({partners.length})
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card className="border-[hsl(210,25%,90%)]">
              <CardHeader>
                <CardTitle>Kontaktanfragen</CardTitle>
                <CardDescription>Alle eingegangenen Nachrichten</CardDescription>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <p className="text-center py-8 text-[hsl(215,15%,45%)]">Keine Kontakte vorhanden</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>E-Mail</TableHead>
                        <TableHead>Nachricht</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contacts.map((contact) => (
                        <TableRow key={contact.id}>
                          <TableCell className="text-sm">{formatDate(contact.created_at)}</TableCell>
                          <TableCell className="font-medium">{contact.name}</TableCell>
                          <TableCell>
                            <a href={`mailto:${contact.email}`} className="text-[hsl(213,80%,35%)] hover:underline">
                              {contact.email}
                            </a>
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={contact.message}>
                            {contact.message}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={contact.status}
                              onValueChange={(value) => handleContactStatusChange(contact.id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">Neu</SelectItem>
                                <SelectItem value="read">Gelesen</SelectItem>
                                <SelectItem value="replied">Beantwortet</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteContact(contact.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card className="border-[hsl(210,25%,90%)]">
              <CardHeader>
                <CardTitle>Terminbuchungen</CardTitle>
                <CardDescription>Alle gebuchten Termine</CardDescription>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-center py-8 text-[hsl(215,15%,45%)]">Keine Termine vorhanden</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Termin</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Kontakt</TableHead>
                        <TableHead>Thema</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell>
                            <div className="font-medium">{booking.date}</div>
                            <div className="text-sm text-[hsl(215,15%,45%)]">{booking.time} Uhr</div>
                          </TableCell>
                          <TableCell className="font-medium">{booking.name}</TableCell>
                          <TableCell>
                            <div>
                              <a href={`mailto:${booking.email}`} className="text-[hsl(213,80%,35%)] hover:underline text-sm">
                                {booking.email}
                              </a>
                            </div>
                            <div className="text-sm text-[hsl(215,15%,45%)]">{booking.phone}</div>
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={booking.topic}>
                            {booking.topic || '-'}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={booking.status}
                              onValueChange={(value) => handleBookingStatusChange(booking.id, value)}
                            >
                              <SelectTrigger className="w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Ausstehend</SelectItem>
                                <SelectItem value="confirmed">Bestätigt</SelectItem>
                                <SelectItem value="completed">Abgeschlossen</SelectItem>
                                <SelectItem value="cancelled">Storniert</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteBooking(booking.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Press Tab */}
          <TabsContent value="press">
            <Card className="border-[hsl(210,25%,90%)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Presse & News</CardTitle>
                  <CardDescription>Artikel verwalten</CardDescription>
                </div>
                <Button onClick={() => openArticleDialog()} className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Neuer Artikel
                </Button>
              </CardHeader>
              <CardContent>
                {pressArticles.length === 0 ? (
                  <p className="text-center py-8 text-[hsl(215,15%,45%)]">Keine Artikel vorhanden</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Titel</TableHead>
                        <TableHead>Kategorie</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pressArticles.map((article) => (
                        <TableRow key={article.id}>
                          <TableCell className="text-sm">{formatDate(article.created_at)}</TableCell>
                          <TableCell className="font-medium max-w-xs truncate">{article.title}</TableCell>
                          <TableCell>
                            <Badge className={
                              article.category === 'news' ? 'bg-blue-100 text-blue-700' :
                              article.category === 'press' ? 'bg-green-100 text-green-700' :
                              'bg-purple-100 text-purple-700'
                            }>
                              {article.category === 'news' ? 'News' : 
                               article.category === 'press' ? 'Presse' : 'Ankündigung'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={article.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                              {article.published ? 'Veröffentlicht' : 'Entwurf'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openArticleDialog(article)}
                                className="text-[hsl(213,80%,35%)] hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleTogglePublish(article.id)}
                                className="text-[hsl(215,15%,45%)] hover:bg-gray-50"
                              >
                                {article.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteArticle(article.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card className="border-[hsl(210,25%,90%)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Partner</CardTitle>
                  <CardDescription>Partner und Adressen verwalten</CardDescription>
                </div>
                <Button onClick={() => openPartnerDialog()} className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Neuer Partner
                </Button>
              </CardHeader>
              <CardContent>
                {partners.length === 0 ? (
                  <p className="text-center py-8 text-[hsl(215,15%,45%)]">Keine Partner vorhanden</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Kategorie</TableHead>
                        <TableHead>Kontakt</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partners.map((partner) => (
                        <TableRow key={partner.id}>
                          <TableCell>
                            <div className="font-medium">{partner.name}</div>
                            <div className="text-sm text-[hsl(215,15%,45%)] max-w-xs truncate">{partner.description}</div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              partner.category === 'lawyer' ? 'bg-blue-100 text-blue-700' :
                              partner.category === 'counseling' ? 'bg-green-100 text-green-700' :
                              partner.category === 'organization' ? 'bg-purple-100 text-purple-700' :
                              'bg-amber-100 text-amber-700'
                            }>
                              {getPartnerCategoryLabel(partner.category)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {partner.phone && <div>{partner.phone}</div>}
                            {partner.email && <div className="text-[hsl(213,80%,35%)]">{partner.email}</div>}
                          </TableCell>
                          <TableCell>
                            <Badge className={partner.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                              {partner.active ? 'Aktiv' : 'Inaktiv'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openPartnerDialog(partner)}
                                className="text-[hsl(213,80%,35%)] hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleTogglePartner(partner.id)}
                                className="text-[hsl(215,15%,45%)] hover:bg-gray-50"
                              >
                                {partner.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeletePartner(partner.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Article Dialog */}
        <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingArticle ? 'Artikel bearbeiten' : 'Neuer Artikel'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={articleForm.title}
                  onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                  placeholder="Artikel-Titel"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subtitle">Untertitel</Label>
                <Input
                  id="subtitle"
                  value={articleForm.subtitle}
                  onChange={(e) => setArticleForm({...articleForm, subtitle: e.target.value})}
                  placeholder="Optionaler Untertitel"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategorie</Label>
                  <Select
                    value={articleForm.category}
                    onValueChange={(value) => setArticleForm({...articleForm, category: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="news">News</SelectItem>
                      <SelectItem value="press">Pressemitteilung</SelectItem>
                      <SelectItem value="announcement">Ankündigung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author">Autor</Label>
                  <Input
                    id="author"
                    value={articleForm.author}
                    onChange={(e) => setArticleForm({...articleForm, author: e.target.value})}
                    placeholder="Autor"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="image_url">Bild-URL (optional)</Label>
                <Input
                  id="image_url"
                  value={articleForm.image_url}
                  onChange={(e) => setArticleForm({...articleForm, image_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Inhalt *</Label>
                <Textarea
                  id="content"
                  value={articleForm.content}
                  onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                  placeholder="Artikel-Inhalt..."
                  rows={10}
                  className="resize-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowArticleDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveArticle} className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white">
                {editingArticle ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Admin;
